import Phaser from 'phaser';
import { hitStop } from '../systems/juice';
import { clampShakeIntensity, reducedHitStopMs } from '../systems/motion-prefs';

/**
 * W3 — Scene-level juice façade.
 *
 * Owns four primitive effects (flashSprite / screenShake / hitStop / scalePunch);
 * call-sites in `horizontal-battle-scene.ts` replace inline shake/flash/pause
 * calls with these methods so tuning + testing have a single seam.
 *
 * Design constraints (per docs/game-pr-specs/W3-juice-layer.md):
 * - All methods are try-guarded for test-harness safety (Phaser HEADLESS).
 * - `preFX.addColorMatrix()` requires Phaser ≥ 3.60 WebGL renderer;
 *   falls back silently to `setTint()` (mid-tier) then `setAlpha()` pulse
 *   (Graphics path) when unavailable.
 *
 * Ships four primitives — call sites wire them in `horizontal-battle-scene.ts`
 * (4 hook sites: enemy hit / player hit / kill / boss die).
 */
/**
 * hitStop 全域冷卻 (boss 2026-06-17「飛彈連發打魔王頓一下頓一下」)。
 * systems/juice.ts 的 hitStop 是 ref-counted:連發命中(homing 飛彈/快射)每發都呼叫 →
 * 凍幀背靠背鏈起來 → scene 幾乎持續凍結 = 連續微頓。此冷卻讓「一陣連打只凍一次」:
 * 冷卻內的重複呼叫直接忽略。值 > 最長單次凍幀(80ms crit)且 < base 射擊間隔(320ms),
 * 所以單發命中仍有打擊感,只擋掉「鏈式」凍幀。純表現,不碰戰力/progress。
 */
const HIT_STOP_COOLDOWN_MS = 140;

export class JuiceLayer {
  private _lastHitStopAtMs = -Infinity;
  constructor(private scene: Phaser.Scene) {}

  /**
   * Flash a sprite/image with a color overlay for `ms` milliseconds.
   *
   * Implementation order (each step try-guarded; first that lands wins):
   *   1. `(target as any).preFX?.addColorMatrix?.()` — Phaser 3.60 WebGL.
   *   2. `(target as any).setTint?.(color)` + clearTint on delay.
   *   3. `(target as any).setAlpha?.(0.4)` + restore (Graphics path).
   *
   * @param target  Any Phaser GameObject with preFX or setTint or setAlpha.
   * @param color   24-bit RGB hex (e.g. 0xff0000 for red hurt flash).
   * @param ms      Duration in milliseconds. Default 80.
   */
  flashSprite(
    target: Phaser.GameObjects.GameObject,
    color: number,
    ms: number = 80,
  ): void {
    if (!target) return;

    // 1. preFX.addColorMatrix (Phaser 3.60+ WebGL)
    //
    // v1.1 battle-flow P0 (boss:「打完卡住」root cause #2): on some WebGL drivers
    // addColorMatrix().saturate() throws「uniform1fv ... cannot be converted to a
    // sequence」DURING the next render — an async GL error that try/catch here
    // CANNOT catch, crashing the frame loop right as the boss dies so ResultScene
    // never mounts. The colorMatrix path is a pure visual nicety; the setTint
    // fallback (step 2) looks nearly identical and is GL-safe. So we SKIP the
    // colorMatrix path entirely and fall through to setTint. (Kept the code path
    // documented rather than deleted so the regression history is clear.)
    const USE_COLOR_MATRIX = false; // disabled — see comment above.
    if (USE_COLOR_MATRIX) {
      try {
        const preFX = (target as unknown as { preFX?: { addColorMatrix?: () => unknown } }).preFX;
        const fx = preFX?.addColorMatrix?.() as
          | { saturate?: (n: number) => void; destroy?: () => void }
          | undefined;
        if (fx) {
          try { fx.saturate?.(1); } catch { /* no-op */ }
          try {
            this.scene.time.delayedCall(ms, () => {
              try { fx.destroy?.(); } catch { /* no-op */ }
            });
          } catch { /* headless: no time/delayedCall */ }
          return;
        }
      } catch { /* fall through */ }
    }

    // 2. setTint fallback (Sprite / Image)
    try {
      const setTint = (target as unknown as { setTint?: (c: number) => void }).setTint;
      if (typeof setTint === 'function') {
        setTint.call(target, color);
        try {
          this.scene.time.delayedCall(ms, () => {
            try {
              const clearTint = (target as unknown as { clearTint?: () => void }).clearTint;
              clearTint?.call(target);
            } catch { /* no-op */ }
          });
        } catch { /* headless */ }
        return;
      }
    } catch { /* fall through */ }

    // 3. setAlpha pulse fallback (Graphics — no setTint)
    try {
      const setAlpha = (target as unknown as { setAlpha?: (a: number) => void; alpha?: number }).setAlpha;
      const originalAlpha = (target as unknown as { alpha?: number }).alpha ?? 1;
      if (typeof setAlpha === 'function') {
        setAlpha.call(target, 0.4);
        try {
          this.scene.time.delayedCall(ms, () => {
            try { setAlpha.call(target, originalAlpha); } catch { /* no-op */ }
          });
        } catch { /* headless */ }
      }
    } catch { /* swallow — test harness */ }
  }

  /**
   * Camera shake. Delegates to `cameras.main.shake(ms, intensity)`.
   *
   * Prefer small intensity (0.005–0.012) for regular hits, larger (0.02) for
   * boss death. Headless / no-camera scenes silently no-op.
   *
   * The requested intensity is routed through `clampShakeIntensity` first:
   * - a K-3 hard ceiling (MAX_SHAKE_INTENSITY) caps the most nauseating
   *   amplitudes (the target audience is 8–9 yr-olds, motion-sickness prone);
   * - when 「減少晃動」 (reduce-motion) is on it is damped to a faint rumble.
   * Both are purely presentational — no 戰力 / progress is touched.
   * If the resolved intensity is 0 (e.g. reduce-motion clamps a tiny shake to
   * nothing) we skip the call entirely so the camera stays perfectly still.
   *
   * @param intensity  Shake intensity 0–1 (requested; gets clamped/damped).
   * @param ms         Duration in milliseconds.
   */
  screenShake(intensity: number, ms: number): void {
    const resolved = clampShakeIntensity(intensity);
    if (resolved <= 0) return;
    try {
      this.scene.cameras.main.shake(ms, resolved);
    } catch { /* headless / no cameras.main */ }
  }

  /**
   * Freeze all scene tweens + set timeScale=0 for `ms` milliseconds.
   *
   * Delegates to `systems/juice.ts hitStop()` which is ref-counted across
   * overlapping calls (QA #C2 — overlapping hitStops only resume when ALL
   * timers drain, not the first one to fire).
   *
   * No-op when `ms <= 0`.
   *
   * When 「減少晃動」 (reduce-motion) is on, the duration is shortened via
   * `reducedHitStopMs` so the freeze-frame does not feel jarring alongside the
   * damped shake. Purely presentational — no 戰力 / progress is touched.
   *
   * @param ms  Pause duration. Typical: 30ms (small hit), 80ms (crit/boss).
   */
  hitStop(ms: number): void {
    if (!(ms > 0)) return;
    // 全域冷卻:擋掉連發命中(飛彈/快射)把 ref-counted hitStop 鏈成連續凍幀的微頓。
    // 只在能取得 scene.time 時套冷卻(headless 無 time → 照舊呼叫,本來就 no-op)。
    let now: number | null = null;
    try { now = this.scene.time?.now ?? null; } catch { /* headless */ }
    if (now != null) {
      if (now - this._lastHitStopAtMs < HIT_STOP_COOLDOWN_MS) return;
      this._lastHitStopAtMs = now;
    }
    const resolved = reducedHitStopMs(ms);
    if (!(resolved > 0)) return;
    try { hitStop(this.scene, resolved); } catch { /* headless */ }
  }

  /**
   * Scale punch on target using `Elastic.Out` ease — "rubber band" pop feel.
   *
   * Reads the target's CURRENT scale as the baseline, overshoots to
   * `baseline * scale` immediately, then tweens scaleX/scaleY back to the
   * baseline over `ms` ms. Based on Godot takeaway ① (TRANS_ELASTIC for
   * correct-answer / kill punch).
   *
   * 2026-06-07: was hard-coded to return to scale 1, which broke any target
   * sized via setDisplaySize() (its normal scale is < 1) — the punch left it
   * blown up to native image px size. The player's evolved tank (evo3 image is
   * the largest source) was the worst offender ("最後一階變大"). Now relative.
   *
   * @param target  Any Phaser GameObject with `setScale` (Image / Sprite / Container).
   * @param scale   Peak scale MULTIPLIER vs current (e.g. 1.3 = 30% overshoot).
   * @param ms      Total tween duration. Default 300ms.
   */
  scalePunch(
    target: Phaser.GameObjects.GameObject,
    scale: number,
    ms: number = 300,
  ): void {
    if (!target) return;
    const t = target as unknown as { scaleX?: number; scaleY?: number; setScale?: (x: number, y?: number) => void };
    let baseX = 1;
    let baseY = 1;
    try {
      if (typeof t.scaleX === 'number' && t.scaleX > 0) baseX = t.scaleX;
      if (typeof t.scaleY === 'number' && t.scaleY > 0) baseY = t.scaleY;
    } catch { /* headless stub */ }
    // 連續進化(evo2→evo3)時先清掉殘留的 punch tween,避免基準被「放大中」的值
    // 記住而永久殘留放大。makeScene 等 stub 無此方法 → 可選鏈安全跳過。
    try { (this.scene.tweens as unknown as { killTweensOf?: (o: unknown) => void }).killTweensOf?.(target); } catch { /* no tweens */ }
    try {
      // 均勻縮放用單參數 setScale(保持與既有測試/呼叫慣例一致)。
      if (baseX === baseY) t.setScale?.(baseX * scale);
      else t.setScale?.(baseX * scale, baseY * scale);
      this.scene.tweens.add({
        targets: target,
        scaleX: baseX,
        scaleY: baseY,
        duration: ms,
        ease: 'Elastic.Out',
        easeParams: [1.2, 0.5], // amplitude, period
      });
    } catch { /* headless / no tweens */ }
  }
}
